import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Bell, CheckCircle2, LayoutDashboard } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { homeApi, type HomeTaskItem } from "@/services/home";
import { adminNotificationsApi, type AdminNotificationItem } from "@/services/adminNotifications";
import { cn } from "@/lib/utils";
// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 顶部导航版式的下拉定位
import { useTopnavDropdown } from "./useTopnavDropdown";
// ═ COMPANY-STRIP END ═

/**
 * 顶部铃铛「待我处理」聚合器（ota_demo_polish_backlog ②·阶段 1）。
 * 复用 GET /api/v1/home 的角色待办（blocks[].tasks）· 同 queryKey 共享 HomePage 缓存 · 零新后端。
 * 角色感知（home 端点已按角色派发）· 深链到各处理页 · 互链交接中心。
 *
 * 阶段 2（BELL2·司导 PWA 事件接入）：下方「动态」段是独立加的真通知表
 * （admin_notifications·GET/POST /api/v1/admin-notifications/*），阶段 1 这一段
 * 的逻辑和 DOM 一行未动——两段各自独立轮询/独立数据源，只在徽标数字上相加。
 */

const SEV_DOT: Record<string, string> = {
  danger: "bg-rose-500",
  warn: "bg-amber-500",
  ok: "bg-emerald-500",
  info: "bg-blue-500",
};

// BELL2 · admin_notifications.severity → 阶段 1 同一套红/黄/蓝视觉语言（铁律十七·不自立新样式）
const FEED_SEV_DOT: Record<AdminNotificationItem["severity"], string> = {
  critical: SEV_DOT.danger,
  warning: SEV_DOT.warn,
  info: SEV_DOT.info,
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "刚刚";
  if (mins < 60) return `${mins} 分钟前`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} 小时前`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return `昨天 ${new Date(iso).toTimeString().slice(0, 5)}`;
  return `${days} 天前`;
}

export function NotificationBell() {
  const role = useAuthStore((s) => s.user?.role);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setOpen(false), []);
  // ═ COMPANY-STRIP BEGIN ═
  const drop = useTopnavDropdown(ref, open, closeMenu);   // SAAS-2b · 顶部导航版式 fixed 定位；侧栏版式原样
  // ═ COMPANY-STRIP END ═
  // ═ COMPANY-ONLY BEGIN ═
  // const drop: { cls: string; style?: undefined } = { cls: "absolute right-0 top-12" };   // 左侧栏版式：下拉照原样 absolute
  // ═ COMPANY-ONLY END ═
  const navigate = useNavigate();
  const qc = useQueryClient();

  // 同 queryKey 共享 HomePage 缓存（在首页时零额外请求，他处一次共享 fetch）
  const home = useQuery({
    queryKey: ["home", role],
    queryFn: () => homeApi.fetch(),
    enabled: !!role,
  });

  // 待办 = 非占位 tasks block 里带深链(href)的 task（真数据·角色派发·永远不脏）
  const todos: HomeTaskItem[] = (home.data?.blocks ?? [])
    .filter((b) => b.kind === "tasks" && !b.placeholder)
    .flatMap((b) => b.tasks)
    .filter((t) => !!t.href);
  // 阶段 1 自己的计数（头部文案「共 X 项 · Y 类」用这个，不受阶段 2 影响）
  const totalItems = todos.reduce((s, t) => s + (t.count ?? 1), 0);

  // BELL2 阶段 2 · admin 站内通知「动态」段（独立数据源·60s 轮询·拿来主义够用不做 websocket）
  const feed = useQuery({
    queryKey: ["admin-notifications-feed"],
    queryFn: () => adminNotificationsApi.list(1, 20),
    enabled: !!role,
    refetchInterval: 60_000,
  });
  const feedItems: AdminNotificationItem[] = feed.data?.items ?? [];
  const feedUnread = feed.data?.unread_count ?? 0;

  // 徽标 = 阶段 1 待办数 + 动态未读数（工单原话）——只有这一个数字是两段合并的。
  const badgeCount = totalItems + feedUnread;
  const badge = badgeCount > 99 ? "99+" : String(badgeCount);

  const handleFeedItemClick = (item: AdminNotificationItem) => {
    setOpen(false);
    if (!item.is_read) {
      adminNotificationsApi.markRead(item.id).then(() => qc.invalidateQueries({ queryKey: ["admin-notifications-feed"] }));
    }
    if (item.deeplink) navigate(item.deeplink);
  };

  const handleMarkAllRead = () => {
    adminNotificationsApi.markAllRead().then(() => qc.invalidateQueries({ queryKey: ["admin-notifications-feed"] }));
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="待我处理"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg hover:bg-slate-100"
      >
        <Bell className="h-[18px] w-[18px] text-slate-600" strokeWidth={2} />
        {badgeCount > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div className={`${drop.cls} z-40 w-[360px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg animate-in fade-in slide-in-from-top-1`.trim()} style={drop.style}>
          {/* 头 */}
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <span className="text-[15px] font-semibold text-slate-900">待我处理</span>
            <span className="text-[12px] text-slate-500">{totalItems > 0 ? `共 ${totalItems} 项 · ${todos.length} 类` : "暂无待办"}</span>
          </div>

          {home.isLoading ? (
            <p className="px-4 py-6 text-center text-[13px] text-slate-400">加载中…</p>
          ) : home.isError ? (
            <button onClick={() => home.refetch()} className="block w-full px-4 py-6 text-center text-[13px] font-medium text-rose-600 hover:bg-rose-50">
              加载失败，点击重试
            </button>
          ) : totalItems === 0 ? (
            <div className="flex flex-col items-center gap-1.5 px-4 py-8 text-center">
              <CheckCircle2 className="h-7 w-7 text-emerald-300" />
              <span className="text-[13px] text-slate-500">暂无待办，一切已处理</span>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              {todos.map((t) => (
                <Link
                  key={t.id}
                  to={t.href!}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3 last:border-0 hover:bg-slate-50"
                >
                  <span className={cn("h-[7px] w-[7px] shrink-0 rounded-full", SEV_DOT[t.severity] ?? SEV_DOT.info)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-slate-900">
                      {t.tag && <span className="mr-1 text-slate-500">{t.tag}</span>}
                      {t.title}
                    </span>
                    <span className="block truncate text-[11px] text-slate-500">{t.meta}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[12px] font-medium text-blue-600">
                    {t.action ?? "去处理"} <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
          )}

          {/* 底：回首页看全部 */}
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-1.5 border-t border-slate-100 px-4 py-2.5 text-[12px] text-slate-500 hover:bg-slate-50"
          >
            <LayoutDashboard className="h-3.5 w-3.5" /> 查看全部待办 · 回首页工作台
          </Link>

          {/* BELL2 阶段 2 · 动态段（司导 PWA 事件：打卡/报销/请假/紧急上报/改派）——
              独立于上面阶段 1 的待办段，自己的头/列表/已读操作。 */}
          <div className="flex items-center justify-between border-t border-b border-slate-100 bg-slate-50/60 px-4 py-2">
            <span className="text-[12px] font-semibold text-slate-500">动态</span>
            {feedUnread > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-[11px] font-medium text-blue-600 hover:underline"
              >
                全部已读
              </button>
            )}
          </div>

          {feed.isLoading ? (
            <p className="px-4 py-5 text-center text-[13px] text-slate-400">加载中…</p>
          ) : feed.isError ? (
            <button onClick={() => feed.refetch()} className="block w-full px-4 py-5 text-center text-[13px] font-medium text-rose-600 hover:bg-rose-50">
              加载失败，点击重试
            </button>
          ) : feedItems.length === 0 ? (
            <div className="px-4 py-5 text-center text-[13px] text-slate-400">暂无新动态</div>
          ) : (
            <div className="max-h-[40vh] overflow-y-auto">
              {feedItems.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleFeedItemClick(n)}
                  className={cn(
                    "flex w-full items-start gap-2.5 border-b border-slate-100 px-4 py-2.5 text-left last:border-0 hover:bg-slate-50",
                    n.is_read && "opacity-60",
                  )}
                >
                  <span className={cn("mt-1.5 h-[7px] w-[7px] shrink-0 rounded-full", FEED_SEV_DOT[n.severity] ?? SEV_DOT.info)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-slate-900">{n.title}</span>
                    <span className="block truncate text-[11px] text-slate-500">{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
